import { Link } from "@tanstack/react-router";
import { useCallback, useState } from "react";

import { Button } from "#/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "#/components/ui/dialog";
import {
  readClientTutorialCompleted,
  writeClientTutorialCompleted,
} from "#/lib/tutorialCompletion";
import type { PlayMapPageReady } from "#/server/game/playMapPage";
import { startMapRunFn } from "#/server/game/writeHandlers";
import { markTutorialCompleted } from "#/server/markTutorialCompleted";

type TutorialPromptDialogProps = {
  mapId: string;
  playerId: string;
  onStarted: (ready: PlayMapPageReady) => void;
};

export function TutorialPromptDialog({ mapId, playerId, onStarted }: TutorialPromptDialogProps) {
  const [isStarting, setIsStarting] = useState(false);

  const startGame = useCallback(async () => {
    setIsStarting(true);
    try {
      const result = await startMapRunFn({ data: { mapId, playerId } });
      onStarted({ status: "ready", ...result });
    } finally {
      setIsStarting(false);
    }
  }, [mapId, onStarted, playerId]);

  const handleSkip = useCallback(async () => {
    writeClientTutorialCompleted();
    try {
      await markTutorialCompleted({ data: {} });
    } catch {}
    await startGame();
  }, [startGame]);

  return (
    <Dialog open>
      <DialogContent showCloseButton={false} className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New to Daily Wars?</DialogTitle>
          <DialogDescription>
            Learn the basics by playing the tutorial before jumping into your first game. You can
            also access the tutorial from the home page.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:justify-end">
          <Button variant="outline" onClick={() => void handleSkip()} disabled={isStarting}>
            Skip
          </Button>
          <Button
            render={<Link to="/tutorial" preload={false} />}
            nativeButton={false}
            disabled={isStarting}
          >
            Play
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function shouldSkipTutorialPrompt(): boolean {
  return readClientTutorialCompleted();
}
